import type { AiScribeStep } from "../types";

const STEPS_FINISHED = new Set(["finished", "finishedRetry", "finishedMerge"]);
/**
 * Returns true if the given step is the recording successfully finished step.
 */
export const isFinishedStep = (step?: AiScribeStep | null): boolean =>
  step ? STEPS_FINISHED.has(step) : false;

const STEPS_ERROR = new Set<AiScribeStep>([
  "errorMicrophoneInitial",
  "errorMicrophoneInterrupted",
  "errorNoRetry",
  "errorTimeoutProcessing",
  "errorWhileProcessing",
  "errorWithRetry",
]);
/**
 * Returns true if the given step is *any* kind of error step.
 */
export const isErrorStep = (step?: AiScribeStep | null): boolean =>
  step ? STEPS_ERROR.has(step) : false;

const STEPS_IDLE = new Set<AiScribeStep>([
  "initial",
  "paused",
  "finished",
  "finishedRetry",
  "cancelingRetry",
  "finishedMerge",
  "cancelingMerge",
  "errorMicrophoneInitial",
  "errorMicrophoneInterrupted",
  "errorNoRetry",
  "errorTimeoutProcessing",
  "errorWhileProcessing",
  "errorWithRetry",
  "unsupportedNote",
]);

/**
 * Returns true if the given step is an "idle" step (e.g. OK to change settings).
 */
export const isIdleStep = (step?: AiScribeStep | null): boolean =>
  step ? STEPS_IDLE.has(step) : false;

/**
 * Returns true if the given step is the "paused" step.
 */
export const isPausedStep = (step?: AiScribeStep | null): boolean =>
  step ? step === "paused" : false;

const STEPS_RECORDING_ACTIVE = new Set<AiScribeStep>([
  "recordingInitializing",
  "recordingActive",
]);
/**
 * Returns true if the given step is an active "recording" step which means we are starting
 * or actively recording with the microphone.
 */
export const isRecordingActiveStep = (step: AiScribeStep): boolean =>
  STEPS_RECORDING_ACTIVE.has(step);

const STEPS_ERROR_RECORDING = new Set<AiScribeStep>([
  "errorNoRetry",
  "errorWithRetry",
]);
/**
 * Returns true if the given step is a "recording error" step, which
 * largely just means an error occurred during recording that did NOT
 * involve a disconnected/faulty microphone. Microphone errors are
 * treated differently since they're caused by issues with the user's
 * hardware and it's up to users to resolve them.
 */
export const isRecordingErrorStep = (step: AiScribeStep): boolean =>
  STEPS_ERROR_RECORDING.has(step);

/**
 * Returns true if the given step is a terminal step (e.g. finished or error)
 * that might display a status banner/marquee or require more user attention.
 */
export const isTerminalStep = (step?: AiScribeStep | null): boolean => {
  if (!step) {
    return false;
  }
  return isFinishedStep(step) || isErrorStep(step);
};
